/**
 * Converting between wall-clock text and instants **in a named zone**.
 *
 * The workstation's own zone is never the right basis for scheduling. A
 * pharmacist typing "09:00" against a Toronto store means 09:00 *in Toronto*,
 * whether they are sitting in Toronto, Vancouver or Bengaluru. Using
 * `new Date('2026-08-04T09:00')` instead silently reads the string in the
 * workstation's zone, which shifts the result by the difference between the two
 * — 9.5 hours from India, enough to move a working-day closure into the middle
 * of the night, where it blocks nothing.
 *
 * Implemented on `Intl` rather than a date library because `packages/shared`
 * carries no dependencies.
 */

/** Wall-clock text with no zone: `YYYY-MM-DDTHH:mm`, as `datetime-local` uses. */
export type WallClockText = string;

interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/**
 * `hourCycle: 'h23'` rather than `hour12: false`, which some engines render as
 * hour "24" at midnight and would push the date a day forward.
 */
function partsIn(instant: Date, timeZone: string): ZonedParts {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);

  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const part = parts.find((candidate) => candidate.type === type);
    return part ? Number(part.value) : 0;
  };

  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
    second: read('second'),
  };
}

/** How far `timeZone` runs ahead of UTC at `instant`, in milliseconds. */
function offsetAt(instant: Date, timeZone: string): number {
  const p = partsIn(instant, timeZone);
  const asIfUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  // Offsets are whole minutes; drop the instant's milliseconds so they cancel.
  return asIfUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/;

/**
 * The instant at which `wall` occurs in `timeZone`.
 *
 * Two passes, because the offset to subtract depends on the answer: the first
 * guess uses the offset at the *UTC* reading of the text, the second corrects it
 * using the offset actually in force at that guess. That second pass is what
 * makes times near a daylight-saving switch land on the right side of it.
 *
 * A time inside a spring-forward gap does not exist; it resolves to the instant
 * the clocks jump to, which is the same forgiving behaviour browsers apply.
 */
export function instantFromZonedWall(wall: WallClockText, timeZone: string): Date {
  const match = WALL_CLOCK.exec(wall);
  if (!match) throw new Error(`Not a wall-clock time: ${wall}`);
  const [, year, month, day, hour = '00', minute = '00'] = match;

  const readAsUtc = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
  );

  const firstGuess = new Date(readAsUtc - offsetAt(new Date(readAsUtc), timeZone));
  return new Date(readAsUtc - offsetAt(firstGuess, timeZone));
}

/**
 * `instant` as `YYYY-MM-DDTHH:mm` on the clock in `timeZone`.
 *
 * Private: nothing outside needs the formatting direction yet, and an exported
 * function with no caller is one more thing to keep true.
 */
function zonedWallText(instant: Date, timeZone: string): WallClockText {
  const p = partsIn(instant, timeZone);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return (
    `${p.year}-${pad(p.month)}-${pad(p.day)}` + `T${pad(p.hour)}:${pad(p.minute)}`
  );
}

/**
 * Milliseconds from local midnight to `instant`, on the clock in `timeZone`.
 *
 * Read off the wall clock rather than subtracted from midnight, and the two are
 * not the same thing. On the spring-forward day 10:00 is only nine elapsed
 * hours after midnight because an hour is missing — so a calendar that places
 * blocks by elapsed time draws that day an hour out of step with its own axis
 * labels, and a drag then resolves to a different time than the one under the
 * pointer. This always answers "what does the clock say".
 */
export function zonedMsOfDay(instant: Date, timeZone: string): number {
  const p = partsIn(instant, timeZone);
  return ((p.hour * 60 + p.minute) * 60 + p.second) * 1000;
}

/** `YYYY-MM-DD` for the calendar day `instant` falls on, in `timeZone`. */
export function zonedDateOf(instant: Date, timeZone: string): string {
  return zonedWallText(instant, timeZone).slice(0, 10);
}

/** `YYYY-MM-DD` for today on the clock in `timeZone`. */
export function zonedToday(timeZone: string, now: Date = new Date()): string {
  return zonedDateOf(now, timeZone);
}

/**
 * The instants bounding one calendar day in `timeZone`, as ISO strings.
 *
 * Computed from the day's own midnight to the next day's, not by adding 24
 * hours: a daylight-saving day is 23 or 25 hours long, and a fixed span would
 * spill an hour of the neighbouring day into the window.
 */
export function zonedDayWindow(
  date: string,
  timeZone: string,
): { from: string; to: string } {
  const start = instantFromZonedWall(`${date}T00:00`, timeZone);
  const nextDay = new Date(`${date}T00:00:00Z`);
  nextDay.setUTCDate(nextDay.getUTCDate() + 1);
  const end = instantFromZonedWall(
    `${nextDay.toISOString().slice(0, 10)}T00:00`,
    timeZone,
  );
  return { from: start.toISOString(), to: end.toISOString() };
}
